import { Component, forwardRef, Input, type OnChanges, type OnInit, type SimpleChanges } from '@angular/core';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { ExpandableListDirective } from '@frontendlib/components/directives/expandable-list.directive';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { SkeletonBlockComponent } from '@frontendlib/components/components/skeleton-block/skeleton-block.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { BaseSearchResponse } from '@modules/common/types/base-search-response';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { ManageContextMenuDirective } from '@modules/tags/directives/manage-context-menu.directive';
import { type TagsManageListState } from '@modules/tags/types/tags-manage-list-state';
import { Tag } from '@modules/tags/types/tag';
import { IncludesByPipe } from '@modules/utils/pipes/array/includes-by.pipe';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AddTagsComponent } from '../add-tags/add-tags.component';
import { TagComponent } from '../tag/tag.component';
import { TagsComponent } from '../tags/tags.component';

@Component({
  selector: 'app-shared-tags',
  templateUrl: '../tags/tags.component.html',
  styleUrls: ['../tags/tags.component.less'],
  standalone: true,
  imports: [
    IconComponent,
    ContextMenuStateIndicatorComponent,
    PopoverDirective,
    TagComponent,
    IncludesByPipe,
    ManageContextMenuDirective,
    ExpandableListDirective,
    // Compiles TagsComponent's template; the skeleton is never drawn on a shared page (`isShared`).
    SkeletonBlockComponent,
    // Compiles TagsComponent's template; its drop predicate refuses every drop on a shared page (`isShared`).
    DragNDropModule,
    forwardRef(() => AddTagsComponent),
  ],
})
export class SharedTagsComponent extends TagsComponent implements OnInit, OnChanges {
  @Input() public stitchItem: StitchItemSummaryDto;

  public override ngOnInit(): void {
    // Don't call super.ngOnInit() to skip recommendations loading
    // Initialize state if not set
    if (!this.state) {
      this.state = {} as TagsManageListState;
    }
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    // When stitchItem changes, trigger data loading
    if ('stitchItem' in changes && changes['stitchItem'].currentValue) {
      const currentId = changes['stitchItem'].currentValue?.id;
      const previousId = changes['stitchItem'].previousValue?.id;

      if (currentId !== previousId) {
        this.expanded.set(false);
        this.page.set(0);
        this.tags.set([]);
      }
      this.searchTags()
        .pipe(takeUntil(this.alive))
        .subscribe(({ items, count }) => this.updateTags(items, count));
    }
  }

  protected override searchTags(): Observable<BaseSearchResponse<Tag>> {
    if (!this.stitchItem) {
      throw new Error('Stitch item is required for shared tags');
    }

    // Sorting/filtering is disabled on shared pages (applying a sort drops the markers — SBUX-539),
    // so ignore any persisted sort/filter state and always fetch in the backend default order.
    return this.tagsService.searchShared(this.stitchItem, {
      offset: this.page() * this.perPage,
      limit: this.perPage,
    });
  }
}
